import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AuthenticationService } from '../../services/authentication.service';
import { ChatImage, ChatRequest, EditTool, ImageChatService, StreamEvent } from '../../services/image-chat.service';

/** The image the modal opens on. */
export interface AiEditSource {
    id: string | number;
    filename?: string;
    caption?: string;
    /** What gets uploaded as the working copy. */
    thumbnail: string;
    /** "1152 × 1152", shown as a badge. */
    size?: string;
    /** Where the edit was opened from, shown under the caption. */
    source?: string;
}

/** One state of the image: the original, or an edit on top of the one before. */
interface EditVersion {
    label: string;
    /** The studio's image id; null for the original until its upload lands. */
    id: string | null;
    url: string;
}

/** One line of this modal's conversation. Nothing from earlier sessions is shown. */
interface ChatLine {
    role: 'user' | 'assistant';
    text: string;
    /** For a result: the version it produced, so the reply can show and select it. */
    version?: EditVersion;
    error?: boolean;
}

/** A tool the modal can run without extra canvas UI (mask, crop box). */
interface QuickTool {
    key: '' | EditTool;
    label: string;
}

/**
 * "Edit with AI" on an image detail page.
 *
 * A compact front for the IP.AI studio, on the image the customer is looking at:
 * the image is uploaded as the studio's working copy, and each edit goes through
 * the same studio calls the full workspace makes — editImage / faceRestore for a
 * picked tool, the streamed chat turn for a free-text instruction. Results come
 * back in one studio session, so "Continue in IP.AI" opens that session with
 * every edit made here already in it.
 *
 * The thread on the right is this modal's conversation only — the request, the
 * reply and the result of each edit made since it opened.
 *
 * Tools that need a canvas (crop, erase, fill, replace background) are left to
 * the full workspace rather than half-built here.
 */
@Component({
    selector: 'ai-edit-modal',
    templateUrl: './ai-edit-modal.component.html',
    styleUrls: ['./ai-edit-modal.component.scss']
})
export class AiEditModalComponent implements OnInit, OnDestroy {

    @Input() image!: AiEditSource;
    /** Off where the page has no wishlist (exclusive accounts). */
    @Input() showWishlist = true;

    @Output() closed = new EventEmitter<void>();
    @Output() wishlist = new EventEmitter<void>();

    @ViewChild('thread') thread?: ElementRef<HTMLElement>;

    readonly tools: QuickTool[] = [
        { key: '', label: 'No tool — describe the edit' },
        { key: 'remove_bg', label: 'Remove background' },
        { key: 'colorize', label: 'Colorize' },
        { key: 'denoise', label: 'Denoise' },
        { key: 'face_restore', label: 'Restore faces' }
    ];

    /** One-click instructions; each runs as a prompt. */
    readonly suggestions = ['Remove background', 'Black & white', 'Warm tones', 'Crop tighter', 'Upscale to 4K'];

    tool: '' | EditTool = '';
    prompt = '';

    versions: EditVersion[] = [];
    /** The version on screen, and the one the next edit builds on. */
    current = 0;

    /** This modal's conversation. */
    lines: ChatLine[] = [];

    /** 0–100: how much of the edited version the before/after slider reveals. */
    split = 50;

    preparing = true;
    prepareFailed = false;
    busy = false;
    step = '';
    helpOpen = false;

    private sessionId: string | null = null;
    private studioUserId = '';
    private currentUser: any;
    private subs: Subscription[] = [];
    private active: Subscription | null = null;

    constructor(private chatService: ImageChatService,
                private authService: AuthenticationService,
                private toastr: ToastrService,
                private router: Router) {
    }

    ngOnInit(): void {
        this.currentUser = this.authService.currentUserValue;
        document.body.classList.add('ai-edit-open');

        this.versions = [{ label: 'Original', id: null, url: this.image.thumbnail }];

        this.subs.push(this.chatService.getStudioUserId().subscribe(
            res => { this.studioUserId = res?.user_id || ''; },
            () => { /* optional: the backend injects it on edit calls */ }
        ));

        this.prepare();
    }

    ngOnDestroy(): void {
        document.body.classList.remove('ai-edit-open');
        this.subs.forEach(s => s.unsubscribe());
        if (this.active) { this.active.unsubscribe(); }
    }

    /**
     * Upload the page's image as the studio's working copy. Fetched through the
     * studio's own proxy: the image host does not allow a cross-origin read.
     * The upload opens the session every later edit lands in.
     */
    prepare(): void {
        this.preparing = true;
        this.prepareFailed = false;

        const name = (this.image.filename || String(this.image.id) || 'image').replace(/\.[a-z0-9]+$/i, '') + '.jpg';

        this.subs.push(this.chatService.fetchImageBlob(this.image.thumbnail).pipe(
            switchMap((blob: Blob) => this.chatService.uploadImage(
                new File([blob], name, { type: blob.type || 'image/jpeg' }), null, this.currentUser))
        ).subscribe(
            res => {
                if (!res?.id) { this.failPrepare(); return; }
                this.versions[0] = { ...this.versions[0], id: res.id, url: res.url || this.versions[0].url };
                this.sessionId = res.session_id || this.sessionId;
                this.preparing = false;
            },
            () => this.failPrepare()
        ));
    }

    private failPrepare(): void {
        this.preparing = false;
        this.prepareFailed = true;
    }

    get original(): EditVersion { return this.versions[0]; }
    get shown(): EditVersion { return this.versions[this.current]; }
    get comparing(): boolean { return this.current > 0; }

    get canApply(): boolean {
        if (this.preparing || this.prepareFailed || this.busy || !this.shown?.id) { return false; }
        return !!this.tool || !!this.prompt.trim();
    }

    /** Whether a version is still in the list — undo past it and a new edit drops it. */
    isLive(v: EditVersion): boolean {
        return this.versions.indexOf(v) > -1;
    }

    useSuggestion(text: string): void {
        this.tool = '';
        this.prompt = text;
        this.apply();
    }

    apply(): void {
        if (!this.canApply) { return; }

        const base = this.shown;
        if (this.tool) {
            const label = (this.tools.filter(t => t.key === this.tool)[0] || { label: this.tool }).label;
            this.say('user', label + (base.id !== this.original.id ? ' · on ' + base.label : ''));
            this.runTool(this.tool, label, base);
        } else {
            const text = this.prompt.trim();
            this.say('user', text + (base.id !== this.original.id ? ' · on ' + base.label : ''));
            this.prompt = '';
            this.runPrompt(text, base);
        }
    }

    /** A picked tool: the same calls the studio's tool buttons make. */
    private runTool(tool: EditTool, label: string, base: EditVersion): void {
        const common = {
            session_id: this.sessionId,
            user_id: this.studioUserId || undefined,
            customer_id: this.currentUser?.customers_id
        };

        const call = tool === 'face_restore'
            ? this.chatService.faceRestore({ ...common, image_id: base.id as string, scale: 2 })
            : this.chatService.editImage({
                ...common,
                tool,
                // remove_bg takes a list; the rest take the one image.
                image_id: tool === 'remove_bg' ? undefined : base.id as string,
                image_ids: tool === 'remove_bg' ? [base.id as string] : undefined
            });

        this.begin('Applying ' + label.toLowerCase() + '…');
        this.active = call.subscribe(
            res => {
                if (res?.session_id) { this.sessionId = res.session_id; }
                this.addVersion(res?.new_images, res?.ai_message);
            },
            err => this.fail(err?.error?.ai_message || err?.error?.message || err?.error?.detail
                || 'That edit did not go through.')
        );
    }

    /**
     * A free-text instruction: one streamed studio turn with the shown version
     * attached. Two kinds of pause can come back before anything is spent: an
     * upscaler choice, answered with the orchestrator's own 'auto' (the customer
     * asked for the upscale by pressing Apply), and a confirmation, which is not
     * answered here — what it asks about belongs in the full workspace.
     */
    private runPrompt(text: string, base: EditVersion, extra: Partial<ChatRequest> = {}): void {
        const req: ChatRequest = {
            session_id: this.sessionId || undefined,
            message: text,
            attached_image_ids: [base.id as string],
            customer_id: this.currentUser?.customers_id,
            user_id: this.studioUserId || undefined,
            ...extra
        };

        let answered = false;
        this.begin('Working on it…');

        this.active = this.chatService.sendMessageStream(req).subscribe({
            next: (ev: StreamEvent) => {
                if (ev.session_id) { this.sessionId = ev.session_id; }

                if (ev.event === 'step') {
                    this.step = ev.label || this.step;
                    return;
                }
                if (ev.event === 'error') {
                    answered = true;
                    this.fail(ev.ai_message || ev.detail || 'That edit did not go through.');
                    return;
                }
                if (ev.event !== 'result') { return; }

                answered = true;

                if (ev.action === 'choose_upscaler') {
                    this.runPrompt(text, base, {
                        upscale_model: 'auto',
                        turn_id: ev.turn_id || ev.pending?.turn_id
                    });
                    return;
                }
                if (ev.action === 'confirm' || ev.job?.job_id) {
                    // The AI's own words first, then why it stops here.
                    this.busy = false;
                    this.step = '';
                    if (ev.ai_message) { this.say('assistant', ev.ai_message); }
                    this.say('assistant', 'This one needs the full workspace — use “Continue in IP.AI” to finish it there.');
                    return;
                }

                this.addVersion(ev.new_images, ev.ai_message);
            },
            error: (err: any) => this.fail(err?.error?.ai_message || err?.error?.message || err?.message
                || 'That edit did not go through.'),
            complete: () => {
                if (!answered) { this.fail('No result came back. Please try again.'); }
            }
        });
    }

    private begin(step: string): void {
        if (this.active) { this.active.unsubscribe(); }
        this.busy = true;
        this.step = step;
        this.scrollThread();
    }

    /**
     * A result. `ai_message` is the AI's reply and is always shown, as the studio
     * shows it; an image, when one came back, becomes the next version under it.
     */
    private addVersion(images: ChatImage[] | undefined, aiMessage?: string): void {
        const img = (images || [])[0];
        const message = (aiMessage || '').trim();
        this.busy = false;
        this.step = '';

        if (!img?.id || !img?.url) {
            // A text-only answer — a question back, or why it could not edit — is
            // a reply, not a failure.
            if (message) {
                this.say('assistant', message);
            } else {
                this.fail('The edit finished without an image. Please try again.');
            }
            return;
        }

        // Editing an earlier version discards the versions after it, like undo
        // followed by a new edit in any editor.
        this.versions = this.versions.slice(0, this.current + 1);
        const version: EditVersion = { label: 'Edit ' + this.versions.length, id: img.id, url: img.url };
        this.versions.push(version);
        this.current = this.versions.length - 1;
        this.split = 50;

        this.say('assistant', message || 'Done — here is ' + version.label + '.', version);
    }

    private fail(message: string): void {
        this.busy = false;
        this.step = '';
        this.say('assistant', message, undefined, true);
    }

    private say(role: 'user' | 'assistant', text: string, version?: EditVersion, error = false): void {
        this.lines.push({ role, text, version, error });
        this.scrollThread();
    }

    /** Keep the newest line in view. After render, so the new height counts. */
    private scrollThread(): void {
        setTimeout(() => {
            const el = this.thread?.nativeElement;
            if (el) { el.scrollTop = el.scrollHeight; }
        });
    }

    show(index: number): void {
        if (this.busy || index < 0 || index >= this.versions.length) { return; }
        this.current = index;
        this.split = 50;
    }

    showVersion(v: EditVersion): void {
        this.show(this.versions.indexOf(v));
    }

    undo(): void {
        if (this.current > 0) { this.show(this.current - 1); }
    }

    /** The studio session, opened in the full workspace with every edit in it. */
    continueInStudio(): void {
        const queryParams = this.sessionId ? { sessionId: this.sessionId } : {};
        this.close();
        this.router.navigate(['/ai'], { queryParams });
    }

    /**
     * Download the version on screen. Only an edit: the original here is the
     * watermarked preview, and the licensed file comes from the page's own
     * download.
     */
    download(): void {
        const v = this.shown;
        if (!this.comparing || !v.id || !this.sessionId) { return; }

        this.subs.push(this.chatService.downloadAsset(this.sessionId, v.id, 'image').subscribe(
            (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = (this.image.filename || String(this.image.id)).replace(/\.[a-z0-9]+$/i, '')
                    + '-' + v.label.toLowerCase().replace(/\s+/g, '-') + '.png';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            },
            () => this.toastr.error('Download failed. Please try again.')
        ));
    }

    close(): void {
        this.closed.emit();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.close();
    }
}
